import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin } from "lucide-react";
import { BUSINESS } from "@/lib/constants";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";

function FooterMark() {
    return (
        <svg viewBox="0 0 40 40" width={36} height={36} aria-hidden="true">
            <circle cx="20" cy="20" r="18" stroke="#28d8e0" strokeWidth="2" fill="none" />
            <circle cx="20" cy="20" r="11" stroke="#28d8e0" strokeWidth="1.6" fill="none" opacity="0.85" />
            <circle cx="20" cy="20" r="5" fill="#28d8e0" />
            <circle cx="20" cy="20" r="2.2" fill="#0a1426" />
        </svg>
    );
}

export default function Footer() {
    return (
        <footer
            className="mt-16 md:mt-24 border-t"
            style={{
                background: "var(--color-bg-alt)",
                borderColor: "var(--color-line)",
                color: "var(--color-text)",
            }}
            data-testid="site-footer"
        >
            {/* Top brand row */}
            <div className="a1-container pt-12 md:pt-16 pb-10 grid gap-10 md:grid-cols-12">
                {/* Brand column */}
                <div className="md:col-span-4">
                    <div className="flex items-center gap-3 mb-4">
                        <FooterMark />
                        <span>
                            <span
                                className="block serif text-lg font-bold tracking-wide"
                                style={{ color: "var(--color-text)" }}
                            >
                                A-1 Screening Services Inc.
                            </span>
                            <span
                                className="block text-[10px] font-bold uppercase tracking-[0.2em]"
                                style={{ color: "var(--color-accent)" }}
                            >
                                Naples & SW Florida
                            </span>
                        </span>
                    </div>
                    <p
                        className="italic text-lg mb-6 leading-snug max-w-sm"
                        style={{ color: "var(--color-text-muted)" }}
                    >
                        "{BUSINESS.brandLine}"
                    </p>
                    <div className="space-y-3 text-sm">
                        <a
                            href={`tel:${BUSINESS.phoneTel}`}
                            className="flex items-center gap-3 no-underline group"
                            style={{ color: "var(--color-accent)" }}
                            data-testid="footer-phone"
                        >
                            <span
                                className="w-9 h-9 rounded-md flex items-center justify-center"
                                style={{ background: "rgba(40, 216, 224, 0.10)" }}
                            >
                                <Phone size={16} strokeWidth={2.4} />
                            </span>
                            <span className="text-xl serif font-bold">{BUSINESS.phone}</span>
                        </a>
                        <a
                            href={`mailto:${BUSINESS.email}`}
                            className="flex items-center gap-3 no-underline"
                            style={{ color: "var(--color-text)" }}
                            data-testid="footer-email"
                        >
                            <span
                                className="w-9 h-9 rounded-md flex items-center justify-center"
                                style={{ background: "var(--color-bg-card)" }}
                            >
                                <Mail size={16} />
                            </span>
                            <span>{BUSINESS.email}</span>
                        </a>
                        <div className="flex items-center gap-3" style={{ color: "var(--color-text-muted)" }}>
                            <span
                                className="w-9 h-9 rounded-md flex items-center justify-center"
                                style={{ background: "var(--color-bg-card)" }}
                            >
                                <Clock size={16} />
                            </span>
                            <span>{BUSINESS.hours}</span>
                        </div>
                        <div className="flex items-center gap-3" style={{ color: "var(--color-text-muted)" }}>
                            <span
                                className="w-9 h-9 rounded-md flex items-center justify-center"
                                style={{ background: "var(--color-bg-card)" }}
                            >
                                <MapPin size={16} />
                            </span>
                            <span>{BUSINESS.primaryCity} (home base)</span>
                        </div>
                    </div>
                    <Link
                        to="/free-estimate"
                        className="a1-btn-primary mt-6"
                        data-testid="footer-cta-estimate"
                    >
                        Get a Free Estimate
                    </Link>
                </div>

                {/* Services */}
                <div className="md:col-span-3">
                    <h4
                        className="text-[11px] font-bold uppercase tracking-[0.18em] mb-4"
                        style={{ color: "var(--color-accent)" }}
                    >
                        Services
                    </h4>
                    <ul className="space-y-2">
                        {SERVICES.map((s) => (
                            <li key={s.slug}>
                                <Link
                                    to={`/services/${s.slug}`}
                                    className="text-sm no-underline"
                                    style={{ color: "var(--color-text-muted)" }}
                                >
                                    {s.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Service Areas */}
                <div className="md:col-span-3">
                    <h4
                        className="text-[11px] font-bold uppercase tracking-[0.18em] mb-4"
                        style={{ color: "var(--color-accent)" }}
                    >
                        Service Areas
                    </h4>
                    <ul className="space-y-2">
                        {LOCATIONS.map((l) => (
                            <li key={l.slug}>
                                <Link
                                    to={`/service-areas/${l.slug}`}
                                    className={`text-sm no-underline ${l.is_primary ? "font-bold" : ""}`}
                                    style={{
                                        color: l.is_primary ? "var(--color-accent)" : "var(--color-text-muted)",
                                    }}
                                >
                                    {l.name}{l.is_primary ? " · Home base" : ""}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Company */}
                <div className="md:col-span-2">
                    <h4
                        className="text-[11px] font-bold uppercase tracking-[0.18em] mb-4"
                        style={{ color: "var(--color-accent)" }}
                    >
                        Company
                    </h4>
                    <ul className="space-y-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
                        <li><Link to="/about" className="no-underline" style={{ color: "inherit" }}>About</Link></li>
                        <li><Link to="/gallery" className="no-underline" style={{ color: "inherit" }}>Gallery</Link></li>
                        <li><Link to="/reviews" className="no-underline" style={{ color: "inherit" }}>Reviews</Link></li>
                        <li><Link to="/faq" className="no-underline" style={{ color: "inherit" }}>FAQ</Link></li>
                        <li><Link to="/blog" className="no-underline" style={{ color: "inherit" }}>Blog</Link></li>
                        <li><Link to="/contact" className="no-underline" style={{ color: "inherit" }}>Contact</Link></li>
                    </ul>
                </div>
            </div>

            {/* Bottom strip */}
            <div className="border-t" style={{ borderColor: "var(--color-line)" }}>
                <div
                    className="a1-container py-5 text-xs flex flex-col md:flex-row md:justify-between gap-3"
                    style={{ color: "var(--color-text-dim)" }}
                >
                    <p>Family-owned. Naples-based. Serving Southwest Florida since 1999.</p>
                    <p className="flex flex-wrap gap-x-5 gap-y-1">
                        <Link to="/privacy-policy" className="no-underline" style={{ color: "var(--color-text-dim)" }}>Privacy Policy</Link>
                        <Link to="/terms" className="no-underline" style={{ color: "var(--color-text-dim)" }}>Terms</Link>
                        <span>© {new Date().getFullYear()} A-1 Screening Services Inc.</span>
                    </p>
                </div>
            </div>
        </footer>
    );
}
